/* eslint-disable no-console */
/* eslint-disable consistent-return */
/* eslint-disable no-use-before-define */
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import intersection from 'lodash/intersection';
import omit from 'lodash/omit';
import isObject from 'lodash/isObject';
import isEmpty from 'lodash/isEmpty';
import flattenDeep from 'lodash/flattenDeep';
import flattenDepth from 'lodash/flattenDepth';
import { executePostSign, executeSignatoryFlow } from '@sureifylabs/ext-frontend-esignature';
import { getSSOTraits } from '@sureifylabs/ext-frontend-identity';
import { axiosInstance } from '../../util/axios';
import {
  UPDATE_QUESTION_ANSWER,
  UPDATE_DATA_SELECTOR_OPTIONS,
  GET_NEW_QUESTION_START,
  SET_CURRENT_BREADCRUMB,
  GET_QUESTION_FAILURE,
  SET_ACTION_MESSAGE,
  TOGGLE_ESIGN_BUTTON,
  SET_PDF_CONFIGS,
  SET_POPUP_DETAILS,
  UPDATE_LAST_FOCUSED_ELEMENT_ID,
  SET_COPYAPP_URL,
  SET_OUTER_LIST_ACTION_BUTTON,
  OVERRIDE_CHILD_QUESTIONS_COMPLETED_FLAG,
  SET_CURRENT_EDITING_LIST_INDICES,
  RESET_CURRENT_EDITING_LIST_INDICES,
  UPDATE_QUESTION_ANSWER_TIME_STAMP,
  USPS_API_START,
  USPS_API_COMPLETE,
  // UPDATE_QUESTION_ANSWER_IN_SOCKET,
} from '../types';
import { setUid } from './configActions';
import router, { pageRouter } from '../../util/router';
import {
  checkAuthTypeAndGetAccessToken,
  getMaskedResponses,
  getPageId,
  getUTCTimestamp,
  isCustomRole,
  stripHtml,
  flattenQuestions,
} from '../../utils';
import { getJWTAuthToken, getAndStoreTokensFromAuth } from './authHandler';
import prepareQuestionsPayload from '../../util/questionFormatter';
import { mockAlertPopup } from '../../mockResponse';
import { MODALTYPES } from '../../cruxComponents/utils';
import HELPER from '../reducers/helper';
import { getParams } from '../../util';
import {
  getAddressValidation,
  postMakeCopyApp,
  executeSSOAuthentication,
} from '../../util/externalServices';
import LocalStorageServices from '../../util/localStorageServices';
import SessionStorageServices from '../../util/sessionStorageServices';
import LoggerApi from '../../LoggerApi';

const { REACT_APP_LIFETIME_URL: LIFETIME_URL } = process.env;
const ESIGNATURE_ROUTE = 'E-SIGNATURE';

const FILE_PATH = 'src/redux/actions/questions.js';

const TRAITS = {
  EMAIL: 'email',
  ROLES: 'roles',
};

const ROLE_MAP = {
  SALES: 'Sales',
};

export const setPopupDetails = (payload) => ({
  type: SET_POPUP_DETAILS,
  payload,
});

/** ****************** SSO AUTH ******************* */

/**
 * return traits if GET /traits call is successful
 * else re-initiate sso in case 401 error is thrown by GET /traits
 * redirect to authError page for other errors
 */
export const getTraitsPostAuthorization = async ({ ssoAuthConfig, dispatch }) => {
  const FUNC_NAME = 'getTraitsPostAuthorization';
  try {
    const traitsMap = await getSSOTraits(LIFETIME_URL);

    // 1. No traits found, redirect to /authError
    if (!(traitsMap && traitsMap.size)) {
      LoggerApi.info({
        module: 'SSO',
        message: 'Retrieved no traits. redirecting to /authError page.',
        data: { traitsMap },
        funcName: FUNC_NAME,
        path: FILE_PATH,
        line: 92,
      });
      pageRouter(dispatch, 'authError');
      return null;
    }

    // 2. Found traits, Throw error if required keys are missing in traits response
    const agentkeysMissingMsg = Object.values(TRAITS).reduce((msg, key) => {
      if (traitsMap && !traitsMap.get(key)) msg += `${key} is missing`;
      return msg;
    }, '');

    if (agentkeysMissingMsg) {
      throw new Error(agentkeysMissingMsg);
    }

    // 3. Found traits, check for Sales role & redirect to /authError if unauthorized
    const userRoles = traitsMap.get(TRAITS.ROLES) || [];

    let isAuthorized = false;
    if (typeof userRoles === 'string') {
      isAuthorized = userRoles === ROLE_MAP.SALES;
    } else if (Array.isArray(userRoles)) {
      isAuthorized = userRoles.includes(ROLE_MAP.SALES);
    }

    if (!isAuthorized) {
      LoggerApi.info({
        module: 'SSO',
        message: 'Retrieved traits with unauthorized role. redirecting to /authError page.',
        data: { traitsMap, userRoles },
        funcName: FUNC_NAME,
        path: FILE_PATH,
        line: 125,
      });
      pageRouter(dispatch, 'authError');
      return null;
    }

    // 4. traits with Sales role, return back traits object or null
    if (traitsMap && traitsMap?.size > 0) return Object.fromEntries(traitsMap);
    if (!traitsMap) return null;
    return traitsMap;
  } catch (err) {
    console.error(err);

    const statusCode = get(err, 'response.data.code');
    const logConfig = {
      module: 'SSO',
      message: 'Failed to retrieve SSO traits.',
      data: { statusCode, code: err?.code, message: err?.message },
      funcName: FUNC_NAME,
      path: FILE_PATH,
    };

    if (statusCode === 401) {
      // re-initiate sso
      LoggerApi.error({
        ...logConfig,
        message: `${logConfig.message} 401 Unauthorized, re-initiating SSO authentication`,
        line: 155,
      });
      await executeSSOAuthentication(ssoAuthConfig);
    } else {
      // Will catch network/timeout/reference/type or any other errors
      LoggerApi.error({
        ...logConfig,
        message: `${logConfig.message} redirecting to /authError page`,
        line: 163,
      });
      pageRouter(dispatch, 'authError');
    }
  }
};

/**
 * Handles storing access token and redirecting user to appropriate path based on traits info
 * @param {Object} ssoAuthConfig
 */
export const processPostSSOAuthentication = (ssoAuthConfig) => async (dispatch) => {
  LocalStorageServices.setLocalValue('isLoggedInWithSSO', true);
  const ssoAccessToken = await checkAuthTypeAndGetAccessToken();

  if (ssoAccessToken) {
    LocalStorageServices.setAccessToken(ssoAccessToken);
    const { SSO_USER_TYPE } = ssoAuthConfig;
    const baseAppUrl = window.location.origin;

    const traits = await getTraitsPostAuthorization({
      ssoAuthConfig,
      dispatch,
    });

    // post successful authorization, redirect to actual application
    if (traits) {
      const actualPath = SessionStorageServices.getLocalValue('redirectAppPath');
      const redirectAppPath = actualPath || `?role=${SSO_USER_TYPE}`;
      const redirectUrl = `${baseAppUrl}${redirectAppPath}`;
      if (redirectUrl) window.location.replace(redirectUrl);
    }
  } else {
    console.log(
      'processPostSSOAuthentication:: Access token not found/expired, redirecting to /authError page...',
    );
    dispatch({
      type: SET_ACTION_MESSAGE,
      payload: {
        actionMessage: {
          type: 'error',
          message: 'We cannot proceed you to the application. Please login again after sometime.',
        },
      },
    });
    pageRouter(dispatch, 'authError');
  }
};

/** ****************** DOCUSIGN ******************* */
/** Function to fetch the saved docusign details for Acquire DB
 * and returns the details as postSignProps
 * to use them in the executePostSign
 * */
const fetchDocusignDetails = async () => {
  const FUNC_NAME = 'fetchDocusignDetails';
  const accessToken = await checkAuthTypeAndGetAccessToken();
  const queryParams = {
    get_envelope_details: 1,
  };
  try {
    const { data } = await axiosInstance.post('/questions', queryParams);
    const {
      response: { signing_id: signingId, envelope_id: envelopeId, feature_name: featureName },
    } = data;

    if (signingId && envelopeId && featureName) {
      const postSignProps = {
        accessToken,
        baseUri: LIFETIME_URL,
        featureName,
        envelopeId,
        signingId,
      };
      LoggerApi.info({
        module: 'fetchDocusignDetails',
        message: 'Docusign details fetched from database',
        path: FILE_PATH,
        funcName: FUNC_NAME,
        line: 246,
      });
      return postSignProps;
    }
  } catch (error) {
    console.error(error);
    LoggerApi.info({
      module: 'saveDocusignDetails',
      message: error,
      path: FILE_PATH,
      funcName: FUNC_NAME,
      line: 257,
    });
  }
};

const postSigningCallback = ({ signingId, envelopeId, error }, dispatch, formData) => {
  const FUNC_NAME = 'postSigningCallback';
  if (error) {
    LoggerApi.error({
      module: 'Docusign - sign_status_error',
      message: 'Received error after sign status call and retries',
      data: JSON.stringify(error),
      path: FILE_PATH,
      funcName: FUNC_NAME,
      line: 271,
    });
  }
  if (envelopeId) formData.envelope_id = envelopeId;
  if (signingId) formData.signing_id = signingId;
  LoggerApi.info({
    module: 'Docusign - sign_status',
    message: 'Received data after sign status call and making questions call',
    data: JSON.stringify(formData),
    path: FILE_PATH,
    funcName: FUNC_NAME,
    line: 282,
  });
  dispatch(getNextQuestionSet('/questions', formData));
};

export const processPostDocusignStatus = (formData) => async (dispatch) => {
  const FUNC_NAME = 'processPostDocusignStatus';
  // Function to fetch the saved docusign details for Acquire DB
  const postSignProps = await fetchDocusignDetails();
  try {
    // Before making /executePostSign
    LoggerApi.info({
      module: 'processPostDocusignStatus',
      message:
        'Redirected from docusign...Executing Post Sign flow, Making /sign-status call from acquire',
      path: FILE_PATH,
      funcName: FUNC_NAME,
      line: 301,
    });
    // Make post sign status call after redirecting from docusign portal
    await executePostSign({
      ...postSignProps,
      postSigningCallback: (signInfo) => postSigningCallback(signInfo, dispatch, formData),
    });
  } catch (signErr) {
    console.error(signErr);
    LoggerApi.info({
      module: 'Docusign - sign_status',
      message: signErr,
      path: FILE_PATH,
      funcName: FUNC_NAME,
      line: 315,
    });
    dispatch(getNextQuestionSet('/questions', formData));
  }
};

/* Callback function to save the signingId and envelopeId in acquire DB before redirecting to doscusign */
const saveDocusignDetails = async ({ featureName, feature }) => {
  const FUNC_NAME = 'saveDocusignDetails';
  const {
    signingId,
    envelope: { envelopeId },
  } = feature;
  const queryParams = {
    featureName,
    signingId,
    envelopeId,
    set_envelope_details: 1,
  };
  try {
    const {
      data: { response },
    } = await axiosInstance.post('/questions', queryParams);
    if (response) {
      LoggerApi.info({
        module: 'saveDocusignDetails',
        message: 'Docusign details saved in database',
        data: response,
        path: FILE_PATH,
        funcName: FUNC_NAME,
        line: 345,
      });
      return true;
    }
    SessionStorageServices.removeLocalValue('show_custom_loader');
    LoggerApi.info({
      module: 'saveDocusignDetails',
      message: 'Docusign details are not saved in database',
      data: response,
      path: FILE_PATH,
      funcName: FUNC_NAME,
      line: 356,
    });
    window.location.replace('/signError/');
  } catch (error) {
    console.error(error);
    SessionStorageServices.removeLocalValue('show_custom_loader');
    LoggerApi.info({
      module: 'saveDocusignDetails',
      message: error,
      path: FILE_PATH,
      funcName: FUNC_NAME,
      line: 363,
    });
    window.location.replace('/signError/');
  }
};

export const executeDocuSign =
  (eSignatureData, extraParams = {}) =>
  async (dispatch) => {
    const lifetimeURL = LIFETIME_URL;

    const accessToken = await checkAuthTypeAndGetAccessToken();

    const { feature, signatory, data, wet_signature: isWetSign } = eSignatureData || {};

    const MODULE = `${isWetSign ? 'Docusign(wet_signature)' : 'Docusign'}`;
    const FUNC_NAME = 'executeDocuSign';

    dispatch({
      type: GET_NEW_QUESTION_START,
      params: {},
    });

    const executeSignatoryFlowProps = {
      accessToken,
      featureName: feature,
      signatoryName: signatory,
      concordiaUrl: lifetimeURL,
      payload: data,
      shouldRedirect: !isWetSign,
      signatoryCallback: saveDocusignDetails, // Added callback function for saving docusign details in DB...not in session
    };

    try {
      // Before making POST /termlife
      LoggerApi.info({
        module: MODULE,
        message: `Executing signatory flow for feature: ${feature}, Making term life call`,
        path: FILE_PATH,
        funcName: FUNC_NAME,
        line: 407,
      });

      // Set termlife payload in session to retry termlife api
      SessionStorageServices.setRetrySignKeys(eSignatureData);

      const signatoryResponse = await executeSignatoryFlow(executeSignatoryFlowProps);

      const { envelope: { envelopeId, preSignEnvelopeId, url } = {}, signingId } =
        signatoryResponse || {};

      // Response of POST /termlife for docusign
      if (!signatoryResponse) {
        LoggerApi.info({
          module: MODULE,
          message: 'Redirecting to docusign... /termlife response -',
          data: signatoryResponse,
          path: FILE_PATH,
          funcName: FUNC_NAME,
          line: 423,
        });
      } else {
        // Response of POST /termlife for wetsign
        LoggerApi.info({
          module: MODULE,
          message: 'Success: post /termlife call response',
          path: FILE_PATH,
          funcName: FUNC_NAME,
          line: 433,
        });
      }

      if (isWetSign) {
        // make post questions call with feature & event=signing_complete
        // will get thankyou repspone back and redirect user to that route
        const postWetSignPayload = {
          envelope_id: envelopeId,
          pre_sign_envelope_id: preSignEnvelopeId,
          signing_id: signingId,
          url,
          event: 'signing_complete',
          wet_signature: isWetSign,
          ...extraParams,
        };
        LoggerApi.info({
          module: MODULE,
          message: 'Making: /questions call to acquire BE for wetsign after temlife success',
          path: FILE_PATH,
          funcName: FUNC_NAME,
          line: 455,
        });
        dispatch(getNextQuestionSet('/questions', postWetSignPayload));
      }
    } catch (error) {
      // Error in /termlife call
      console.error('Error in executing signatory flow', error);
      LoggerApi.error({
        module: MODULE,
        message: `Error in executing signatory flow - ${error?.message}`,
        data: error,
        path: FILE_PATH,
        funcName: FUNC_NAME,
        line: 468,
        stacktrace: error?.stack,
      });
      // clear retry termlife keys once termlife call is failed
      SessionStorageServices.removeRetrySignKeys();
    }
  };

// Handles display of Error Action Message, being sent from the backend
export const handleActionMessage = (response) => {
  return (dispatch) => {
    let errorMessage = '';
    switch (response.status) {
      case 200: {
        errorMessage = get(response, 'data.errors.userMessage');
        break;
      }
      default: {
        errorMessage = get(response, 'response.data.errors');
        // Fetch userMessage message from errors object if errors is not of type string.
        if (typeof errorMessage !== 'string') {
          errorMessage = get(response, 'response.data.errors.userMessage');
        }
        break;
      }
    }
    // Sets error message on network disconnections
    const hasNetworkRetry = get(response, 'config.axios-retry.retryCount', 0);
    if (!errorMessage && hasNetworkRetry > 0) {
      errorMessage = 'Your internet connection is unstable.';

      const responseCode = get(response, 'response.status') || response.status;
      const isServerError = Number(responseCode) % 500 <= 11;
      if (isServerError) {
        errorMessage = 'We are experiencing technical difficulties. Please try after some time!';
      }

      // Forcefully displays the popup on network disconnections
      dispatch(
        setPopupDetails({
          question: mockAlertPopup(errorMessage),
          overlayBgColor: 'rgba(255,255,255,0.5)',
          show: true,
          force: true,
          handleClick: () => {
            const url = get(response, 'config.url', '');
            const data = get(response, 'config.data', '');
            if (url) {
              dispatch(getNextQuestionSet(url, JSON.parse(data), {}));
            }
          },
          hasBorder: true,
          showModalClose: false,
          label: 'Retry',
          type: MODALTYPES.retry,
        }),
      );
    }
    dispatch({
      type: GET_QUESTION_FAILURE,
    });
    dispatch({
      type: SET_ACTION_MESSAGE,
      payload: {
        actionMessage: {
          type: 'error',
          message: errorMessage || 'Some Error Occured!',
        },
      },
    });
  };
};

/**
 * Run all direct actions based on questions api response
 */
const processPostQuestionsApiSuccess = (args) => async (dispatch) => {
  const { response, currentStatus } = args || {};
  const dataAction = get(
    response,
    'data.response.other_params.other_next_button_config.data-action',
    {},
  );

  const { type, body } = dataAction;
  if (type === 'COPY_APP' && body?.party_id) {
    const redirectUrl = await postMakeCopyApp(body.party_id);
    if (!redirectUrl) return;
    dispatch({
      type: SET_COPYAPP_URL,
      payload: redirectUrl,
    });
  }

  const eSignatureData = get(response, 'data.response.data', null);
  const isWetSign = get(response, 'data.response.data.wet_signature', false);

  // Execute docusign function when route=e-signature or wet_signature=true
  if (currentStatus === ESIGNATURE_ROUTE || isWetSign) {
    if (!eSignatureData) {
      console.error('No Esignature data found');
      LoggerApi.info({
        module: `${isWetSign ? 'Docusign(wet_signature)' : 'Docusign'}`,
        message: 'No Esignature data found',
        path: FILE_PATH,
        funcName: 'processPostQuestionsApiSuccess',
        line: 471,
        data: { currentStatus, isWetSign },
      });
      return;
    }
    dispatch(executeDocuSign(eSignatureData));
  }
};

// export const updateQuestionAnswerInSocket = (questionId, isReflexive = false) => {
//   return (dispatch) => {
//     dispatch({
//       type: UPDATE_QUESTION_ANSWER_IN_SOCKET,
//       payload: {
//         questionId,
//         isReflexive,
//       },
//     });
//   };
// };

export const updateQuestionAnswerTimeStamp = (
  questionId,
  timeStampData,
  sectionIndex,
  // isPrefilled,
) => {
  return (dispatch, getState) => {
    // ETL actions might be dispatched while timeout response is being rendered to screen
    // Once route is changed to /timeout disable etl actions
    if (process.env.REACT_APP_ENABLE_ETL === 'true' && window.location.pathname !== '/timeout') {
      try {
        const currentState = getState();
        const { activeSubsectionId } = currentState.questions;
        dispatch({
          type: UPDATE_QUESTION_ANSWER_TIME_STAMP,
          payload: {
            questionId,
            timeStampData,
            activeSubsectionId,
            sectionIndex,
          },
        });
      } catch (e) {
        console.log(e.message);
        console.error(e);
      }
    }
  };
};

/** ****************** CYBERSOURCE PAYMENT ******************* */

/**
 * Make POST request and redirect to cybersource dashboard based on signed payload
 * @param {Object} params cybersource signed payload object
 */
function initiateCybersourcePayment(params) {
  const formEl = document.createElement('form');
  formEl.setAttribute('method', 'POST');
  formEl.setAttribute('action', process.env.REACT_APP_CYBERSOURCE_API);

  Object.keys(params).forEach((key) => {
    const inputEl = document.createElement('input');
    inputEl.setAttribute('type', 'hidden');
    inputEl.setAttribute('name', key);
    inputEl.setAttribute('value', params[key]);
    formEl.appendChild(inputEl);
  });

  // const redirectPath = window.location.pathname + window.location.search
  // sessionStorage.setItem('redirectAppPath', redirectPath)

  document.body.appendChild(formEl);
  formEl.submit();
}

/** ****************** HIT QUESTIONS API ******************* */

const makeGetNextQuestionSetCall = (args) => async (dispatch) => {
  const { url, updatedFormData, params } = args || {};
  // clear retry docusign keys in session
  if (updatedFormData.wet_signature || window.location.pathname !== '/e-signature') {
    SessionStorageServices.removeRetrySignKeys();
  }
  const FUNC_NAME = 'makeGetNextQuestionSetCall';
  try {
    const response = await axiosInstance.post(url, updatedFormData);
    const signedPayload = get(response, 'data.response.other_params.cybersource_signed_payload');
    if (signedPayload) {
      LoggerApi.info({
        module: 'Cybersource',
        message: 'Redirecting to cybersource portal for payment',
        funcName: FUNC_NAME,
        path: FILE_PATH,
        line: 541,
      });
      initiateCybersourcePayment(signedPayload);
    } else {
      const currentStatus = get(
        response,
        'data.response.other_params.current_status',
        '',
      ).toUpperCase();

      const isDocusignComplete = getParams('event') === 'signing_complete';
      // docusign loader
      if (isDocusignComplete || currentStatus === 'KNOCKOUT') {
        SessionStorageServices.removeLocalValue('show_custom_loader');
      }
      if (isDocusignComplete) {
        // Log exit page message post docusign signing complete
        const postDocusignHtml = get(
          response,
          'data.response.data.questionnaire.questions.questions[0].questions[0].question_text',
        );
        const postDocusignText = stripHtml(postDocusignHtml);
        LoggerApi.info({
          module: 'Docusign_signing_complete',
          message: `Rendering response to screen with currentStatus ${currentStatus}`,
          data: { text: postDocusignText },
          path: FILE_PATH,
          funcName: FUNC_NAME,
          line: 557,
        });
      }
      // Will receive this flag in e-signature response for docusign OR wet sign response
      // Show loader when show_custom_loader is true
      const customLoader = get(response, 'data.response.other_params.show_custom_loader', false);
      if (customLoader) {
        SessionStorageServices.setLocalValue('show_custom_loader', customLoader);
      }

      const identity = get(response, 'config._identity', 'invalid');
      const isIllustrationRedirect = get(
        response,
        'data.response.other_params.illustration',
        false,
      );
      const documentUrl = get(response, 'data.response.other_params.get_document_url', false);

      // identity check, if response is valid, show the requested page,
      // else /inactivesession response is handled from server side.
      if (identity === 'valid' && isIllustrationRedirect && documentUrl) {
        window.location.replace(documentUrl);
      } else if (identity === 'valid') {
        const uid = get(response, 'data.response.other_params.uid', '');
        router(dispatch, uid, params.isReflexive, response);
        dispatch(setUid(uid));
        dispatch({
          type: `GET_${currentStatus}_SUCCESS`,
          payload: get(response, 'data.response', null),
          params,
        });

        // Set reflexive list index after reflexive api call success
        if (params.isReflexive && params.isListEditAction) {
          dispatch({
            type: 'SET_REFLEXIVE_LIST_INDEX',
            index: params.isList ? params.listIdx : -1,
            id: params.isList ? params.list_question_id : '',
          });
        }

        dispatch(processPostQuestionsApiSuccess({ response, currentStatus }));
      }
    }
  } catch (error) {
    console.error(error);
    let errorMessage = get(error, 'response.data.errors');
    // Fetch userMessage message from errors object if errors is not of type string.
    if (typeof errorMessage !== 'string') {
      errorMessage = get(error, 'response.data.errors.userMessage', 'Some Error Occured!');
    }
    LoggerApi.error({
      module: '/questions',
      message: `Error: ${errorMessage}`,
      data: get(error, 'response.data.errors'),
      path: FILE_PATH,
      funcName: FUNC_NAME,
      line: 605,
    });
    dispatch({
      type: GET_QUESTION_FAILURE,
    });
    dispatch({
      type: SET_ACTION_MESSAGE,
      payload: {
        actionMessage: {
          type: 'error',
          message: errorMessage,
        },
      },
    });
  }
};

const defaultNextSetParams = {
  isReflexive: false,
  sectionIndex: 0,
  validate: false,
  isListAddAction: false,
  reload: true,
  actionProps: {},
  breadcrumbNavigation: false,
  actionMessage: { type: '', message: '' },
  maskedQArray: [],
  listActionId: undefined,
};

export function getNextQuestionSet(url, formData, params = defaultNextSetParams) {
  return async (dispatch, getState) => {
    const state = getState();
    const {
      questions: { role },
    } = state;
    const queryUid = get(state, 'router.location.query.uid', '');
    const roleParams = {};
    if (role) {
      roleParams.role = role;
    }
    const updatedFormData = queryUid
      ? { ...formData, uid: queryUid, ...roleParams }
      : { ...formData, ...roleParams };

    if (window.location.pathname === '/inactivesession') {
      return;
    }
    if (formData?.q_id) {
      params.activeReviewedGroupId = formData.q_id;
      SessionStorageServices.setLocalValue('activeReviewedGroupId', formData.q_id);
    }

    dispatch({
      type: GET_NEW_QUESTION_START,
      updatedFormData,
      params,
    });

    // dispatch focus action only for list buttons
    const isPageGoingToChange =
      params.prev_page_flag || (params.next_page_flag === 1 && !params.index_modify_flag);
    if ((!params.isReflexive && !isPageGoingToChange) || params.listActionId) {
      dispatch({
        type: UPDATE_LAST_FOCUSED_ELEMENT_ID,
        payload: {
          id: params.listActionId || 'btn-continue',
        },
      });
    }

    const ssoAuthConfig = get(state, 'auth.login.ssoAuthConfig', {});
    const isSsoUser = ssoAuthConfig.ENABLE && getParams('role') === ssoAuthConfig.SSO_USER_TYPE;

    // check for token expiry
    const accessToken = await checkAuthTypeAndGetAccessToken();
    if (accessToken) {
      dispatch(
        makeGetNextQuestionSetCall({
          url,
          updatedFormData,
          params,
        }),
      );
    } else if (isSsoUser) {
      // persist path & queryParams in session to redirect post successful sso
      const { pathname, search } = window.location;
      const URL = pathname.concat(search);
      SessionStorageServices.setLocalValue('redirectAppPath', URL);
      // trigger sso auth flow
      await executeSSOAuthentication(ssoAuthConfig);
    } else {
      // make auth call & store fresh tokens
      const _accessToken = await dispatch(getAndStoreTokensFromAuth());
      if (_accessToken) {
        dispatch(
          makeGetNextQuestionSetCall({
            url,
            updatedFormData,
            params,
          }),
        );
      }
    }
  };
}

export const saveListQuestionResponses = (listId, sectionIndex, parentListIndex) => {
  return (dispatch) => {
    dispatch({
      type: OVERRIDE_CHILD_QUESTIONS_COMPLETED_FLAG,
      payload: {
        listId,
        sectionIndex,
        parentListIndex,
      },
    });
  };
};

export const getMockQuestionSet = (response) => {
  return (dispatch) => {
    const currentStatus = get(
      response,
      'data.response.other_params.current_status',
      '',
    ).toUpperCase();
    router(dispatch, null, false, response);
    dispatch({
      type: `GET_${currentStatus}_SUCCESS`,
      payload: get(response, 'data.response', null),
      params: {},
    });
  };
};

export const getSectionAnswers = (currentBreadcrumb) => {
  let questionList = currentBreadcrumb.reduce((acc, section) => {
    return [...acc, ...section.questionList];
  }, []);
  questionList = questionList.filter((question) => question.presentation_type !== 'review');
  const errorsList = currentBreadcrumb.reduce((acc, section) => {
    return [...acc, ...section.errorsArray];
  }, []);
  const answersArray = [];
  currentBreadcrumb.forEach((section) =>
    section.answersArray.forEach((answerObj) => {
      const question = questionList.find((q) => q.question_id === answerObj.question_id);
      const isRequiredInPayload = get(question, 'is_required_in_payload', false);
      const isRequired = get(question, 'validations.required', false);
      const isReflexive = get(answerObj, 'is_reflexive', false);
      const qType = get(question, 'question_type', false);
      const optionalDateAns = !isRequired && qType === 'date' && answerObj.answer === '';
      if (
        answerObj.answer ||
        answerObj.answer === 0 ||
        optionalDateAns ||
        (question && !answerObj.answer && (isRequiredInPayload || isReflexive) && answerObj.hasEdit)
      ) {
        const hasError = errorsList.find((errObj) => errObj.question_id === answerObj.question_id);
        answersArray.push({
          ...answerObj,
          ...(hasError && { question_status: 'invalid' }),
        });
      }
    }),
  );
  return {
    answersArray,
    questionList,
  };
};

export const getAnsweredListQuestion = (currentBreadcrumb) => {
  const answeredListQuestion = currentBreadcrumb.reduce((acc, section) => {
    return [...acc, ...section.answeredListQuestion];
  }, []);
  return answeredListQuestion;
};

const formatAuthAnswers = (answersArray) => {
  const reducer = (accumulator, currentValue) => {
    accumulator[currentValue.question_id] = currentValue.answer;
    return accumulator;
  };
  return answersArray.reduce(reducer, {});
};

export const submitAuthQuestion = () => {
  return (dispatch, getState) => {
    const state = getState();
    const { questions } = state;
    const { activeSubsectionId } = questions;
    const currentSection = questions.sections[activeSubsectionId];
    const { answersArray } = getSectionAnswers(currentSection);

    const destinationURL = get(state, 'auth.destinationURL', '/');
    const loginURL = get(state, 'auth.login.loginURL', '/auth');
    let reqParams;
    if (!isEmpty(answersArray)) {
      reqParams = formatAuthAnswers(answersArray);
    }
    dispatch(
      getJWTAuthToken(loginURL, reqParams, {
        redirectTo: destinationURL,
      }),
    );
  };
};

const insertMissingAnswers = (answersArray, questionList, isReflexive) => {
  return questionList.reduce((acc, question) => {
    const alreadyAnsweredQuestionObject = answersArray.filter(
      (item) => item.question_id === question.question_id,
    );
    if (
      question.display_type === 'breadcrumb' ||
      (question.display_type === 'list' && question.submission_type === 'all')
    ) {
      return acc;
    }
    if (alreadyAnsweredQuestionObject.length) {
      acc.push(...alreadyAnsweredQuestionObject);
      return acc;
    }
    if (question.question_id && question.display_type) {
      if (!isReflexive)
        acc.push({
          question_id: question.question_id,
          answer: '',
        });
    }
    return acc;
  }, []);
};

const appendListCompletionFlag = (questions, answeredListQuestion) => {
  answeredListQuestion.map((qId) => {
    if (!questions.find((question) => question.question_id === qId)) {
      questions.push({
        question_id: qId,
        answer: [],
      });
    }
    return null;
  });
  const appended = questions.map((question) => {
    if (answeredListQuestion.indexOf(question.question_id) > -1) {
      return {
        ...question,
        answer: [],
        extra_params: {
          list_completed_flag: true,
        },
      };
    }
    return question;
  });
  return appended;
};

const getNListAnswers = (currentSubsection) => {
  const { answersArray } = currentSubsection;
  return answersArray.filter((a) => a.question_id.match(/_\$ureify_/));
};

const formatListQuestions = (questions) => {
  const listQs = questions.filter((q) => q.listParentId);
  const otherQs = questions.filter((q) => !q.listParentId);
  const extraParams = {
    list_completed_flag: false,
  };
  if (listQs && listQs.length) {
    const { listParentId, listIndex } = listQs[0];
    const answer = [];
    if (Number.isInteger(listIndex) && listIndex >= 0) {
      extraParams.current_list_answer_index = listIndex;
    }
    listQs.forEach((q) => {
      const temp = { question_id: q.question_id, answer: q.answer };
      answer.push(temp);
    });
    return [
      {
        question_id: listParentId,
        answer,
        extra_params: extraParams,
      },
      ...otherQs,
    ];
  }
  return questions;
};

/**
 * @description Prepares payload for auditing timestamps and browser specific info.
 * @param {*} meta
 * @param {*} currentSubsection
 */
const prepareAuditPayload = (meta, currentSubsection) => {
  const audit = get(currentSubsection, 'audit', []);
  const currentUTCTimestamp = getUTCTimestamp();
  const onloadTimestamp = get(currentSubsection, 'onload_timestamp', '');
  const userAgent = `${get(meta, 'userAgent.name', '')} v${get(meta, 'userAgent.version', '')}`;

  return {
    browser_info: userAgent,
    onload_timestamp: onloadTimestamp,
    onsubmit_timestamp: currentUTCTimestamp,
    audit,
  };
};

/** ****************** USPS ADDRESS MATCHING ******************* */

const getModifiedAddress = (uspsQuestions, apiResponse, isUSPSApiResponse) => {
  const { ADDRESS, CITY, STATE, ZIPCODE } = uspsQuestions;
  const { address_line: addressLine, city, state, zip } = apiResponse || {};
  const option = {
    address: {
      value: isUSPSApiResponse ? addressLine : ADDRESS.answer,
      question_id: ADDRESS.questionId,
      sectionIndex: ADDRESS.sectionIndex,
    },
    city: {
      value: isUSPSApiResponse ? city : CITY.answer,
      question_id: CITY.questionId,
      sectionIndex: CITY.sectionIndex,
    },
    state: {
      value: isUSPSApiResponse ? state : STATE.answer,
      question_id: STATE.questionId,
      sectionIndex: STATE.sectionIndex,
    },
    zip: {
      value: isUSPSApiResponse ? zip : ZIPCODE.answer,
      question_id: ZIPCODE.questionId,
      sectionIndex: ZIPCODE.sectionIndex,
    },
  };

  return option;
};

const displayUSPSAddressModal = async (dispatch, uspsQuestions, uspsNextPage, sectionIndex) => {
  const { ADDRESS = {}, CITY = {}, STATE = {}, ZIPCODE = {} } = uspsQuestions || {};
  dispatch({
    type: USPS_API_START,
    payload: {
      sectionIndex,
    },
  });
  const data = await getAddressValidation({
    addressLine: ADDRESS.answer,
    zip: ZIPCODE.answer,
    city: CITY.answer,
    state: STATE.answer,
  });
  dispatch({
    type: USPS_API_COMPLETE,
  });
  if (data) {
    dispatch({
      type: 'SHOW_USPS_MODEL',
      payload: {
        uspsNextPage,
        options: [
          {
            id: 'actual',
            label: 'Actual Address',
            actual: getModifiedAddress(uspsQuestions),
          },
          {
            id: 'suggested1',
            label: 'Suggested Address',
            suggested: getModifiedAddress(uspsQuestions, data, true),
          },
        ],
      },
    });
    return true;
  }
  return false;
};

const executeUSPSFlow = async ({
  dispatch,
  uspsConfig,
  answersArray,
  uspsNextPage,
  sectionIndex,
}) => {
  const { uspsQuestions, showUSPSAddressModel } = uspsConfig || {};

  const uspsQuestionsList = Object.values(uspsQuestions || {});
  if (uspsQuestionsList.length === 0 || showUSPSAddressModel) {
    dispatch({
      type: 'CLOSE_USPS_MODEL',
    });
    return false;
  }

  // Adding modified answer to respective uspsQuestions object
  // and checking if answers are modified ( Execute USPS flow only if answers are modified )
  const hasChangeInUSPSAnswers = uspsQuestionsList.reduce((acc, q) => {
    const answerObj = answersArray.find((ans) => ans.question_id === q.questionId);
    if (answerObj) {
      const { answer } = answerObj;
      acc = acc || q.answer !== answer;
      q.answer = answer;
    }
    return acc;
  }, false);

  // awaited displayUSPSAddressModal so that if api fails return false
  // and current action (reflexive or next page call) will continue.
  if (hasChangeInUSPSAnswers) {
    const isDisplayed = await displayUSPSAddressModal(
      dispatch,
      uspsQuestions,
      uspsNextPage,
      sectionIndex,
    );
    LoggerApi.info({
      module: 'USPS',
      message: `post address validation - ${
        isDisplayed ? 'showing address suggestion modal' : 'api failed - not showing modal'
      }`,
      data: { questions: uspsQuestions, pageId: window.location.pathname, answersArray },
      funcName: 'executeUSPSFlow',
      path: FILE_PATH,
    });
    return isDisplayed;
  }
  return false;
};

export const submitSectionQuestion = (
  sectionIndex,
  otherParams = {
    isReflexive: false,
    sectionIndex: 0,
    validate: false,
    actionProps: {},
    removeIndex: {},
    calculateClick: false,
    csrConfig: null,
    onClick: '',
    attempt: 0,
    requiredQuestions: [],
    uspsNextPage: false,
    isContingentBenf: false,
    listIndex: null,
    listParentId: null,
    questionId: null,
  },
  action = '',
  url = '/questions',
) => {
  return async (dispatch, getState) => {
    const state = getState();
    const { questions, meta, uid } = state;
    const { activeSubsectionId, uspsConfig } = questions;
    const pageSeqNumber = get(questions, 'currentPageSeqNumber', 1);
    const currentSection = questions.sections[activeSubsectionId];

    const { isList, isContingentBenf } = otherParams;
    const sectionIndx = isContingentBenf ? 1 : sectionIndex;

    const currentSubsection = currentSection[sectionIndx];
    const { completed, questionList: currentQuestionList } = currentSubsection;
    const hasModal = get(currentSubsection, 'otherParams.has_modal', false);

    let allQuestions = [];

    // const isCsrKey = Object.keys(questions.csrConfig)?.find(
    //   (key) => questions.csrConfig[key]?.enable,
    // );
    const isRequoteScreen = activeSubsectionId?.toLowerCase() === 'applicationrequotebreadcrumb';
    if ((getPageId() === 'quote' || isRequoteScreen) && otherParams.onClick) {
      dispatch({
        type: UPDATE_LAST_FOCUSED_ELEMENT_ID,
        payload: {
          id: otherParams.onClick,
        },
      });
    }

    if (isList && otherParams.listIndex === null) {
      otherParams.listIndex = 0;
    }
    const { answersArray, questionList } = getSectionAnswers(currentSection);
    if (
      !otherParams.isReflexive &&
      (await executeUSPSFlow({
        dispatch,
        uspsConfig,
        answersArray,
        uspsNextPage: otherParams.uspsNextPage,
        sectionIndex,
      }))
    ) {
      return;
    }

    const { questionList: allQs, answerList, errorList } = getCompiledQuestionsAnswersErrors(state);
    if (otherParams.isReflexive || otherParams.validate) {
      if (otherParams?.csrConfig?.ctaBtnId && otherParams.isReflexive) {
        const UNEDITABLE_FIELDS = ['group', 'question_group', 'label', 'button'];
        const revisedQs = allQs.filter(
          (q) =>
            get(q, 'properties.cta_button.value') === otherParams.csrConfig.ctaBtnId &&
            !UNEDITABLE_FIELDS.includes(q.question_type),
        );

        const allAnswers = revisedQs
          .map((q) => {
            const answer = answerList.find((a) => a.question_id === q.question_id);
            const hasError = errorList.find((errObj) => errObj.question_id === answer.question_id);
            return {
              ...answer,
              ...(hasError && { question_status: 'invalid' }),
            };
          })
          .filter((a) => a.answer || (a.hasEdit && (a.answer === '' || a.answer)));

        allQuestions = allAnswers.filter(
          (a) => !allQs.find((q) => q.question_id === a.question_id)?.is_hidden,
        );
      } else {
        allQuestions = answersArray.filter(
          (a) => !allQs.find((q) => q.question_id === a.question_id)?.is_hidden,
        );
      }
    } else {
      allQuestions = [
        ...insertMissingAnswers(answersArray, questionList),
        ...getNListAnswers(currentSubsection),
      ];
    }
    // Filters out masked responses from the allQuestions (depends on hasEdit flag)
    const maskedQArray = getMaskedResponses(questionList, answersArray);
    if (maskedQArray.length) {
      const maskedQIds = [];
      maskedQArray.forEach((arr) => {
        const answeredObject = answersArray.filter(
          (item) => item.question_id === arr.question_id && !item.hasEdit,
        );
        if (answeredObject.length) maskedQIds.push(arr.question_id);
      });
      allQuestions = allQuestions.filter((q) => !maskedQIds.includes(q.question_id));
    }

    allQuestions = allQuestions.map((q) => omit(q, 'hasEdit'));
    allQuestions = allQuestions.map((q) => omit(q, 'hasMask'));

    let listQId = '';
    let listIdx = null;
    const listQ = allQuestions.filter((q) => q.listParentId);
    const isListAddAction = !!listQ.length;
    const isListEditAction = !!listQ.filter(
      (q) => Number.isInteger(q.listIndex) && q.listIndex >= 0,
    ).length;
    if (listQ.length) {
      const { listParentId, listIndex } = listQ[0];
      listQId = listParentId;
      listIdx = listIndex;
    }

    // Don't add list_completed_flag in case of list edit action
    if (!isListEditAction) {
      const answeredListQuestion = getAnsweredListQuestion(currentSection);
      allQuestions = appendListCompletionFlag(allQuestions, answeredListQuestion);
    }
    allQuestions = formatListQuestions(allQuestions);

    let payload = prepareQuestionsPayload(
      otherParams.requiredQuestions && otherParams.requiredQuestions.length
        ? otherParams.requiredQuestions
        : allQuestions,
      // allQuestions,
      state.meta.listRelation[activeSubsectionId],
      otherParams.removeIndex,
    );

    // Checking if there is any html string and removing its object in payload to fix cloudfare issue
    /* eslint-disable no-param-reassign */
    payload = payload.filter(
      (q) => /<([A-Za-z][A-Za-z0-9]*)\b[^>]*>(.*?)<\/\1>/.test(q.answer) === false,
    );

    let reqParams = {
      uid,
      page_sequence_number: pageSeqNumber,
      questions: payload,
      ...(otherParams.onClick && { onClick: otherParams.onClick }),
      ...(otherParams.attempt && { attempt: otherParams.attempt }),
      ...(otherParams.illustration === 'true' && { illustration: 'true' }),
    };

    // Appends audit info with request
    // https://sureify.atlassian.net/browse/AS-1183
    const isAuditRequired = get(currentSubsection, 'otherParams.audit_required', false);
    if (isAuditRequired) {
      reqParams = {
        ...reqParams,
        audit_info: prepareAuditPayload(meta, currentSubsection),
      };
    }

    if (!hasModal && completed && !otherParams.onClick) {
      reqParams = {
        ...reqParams,
        index_modify_flag: 1,
      };
    } else {
      reqParams = {
        ...reqParams,
        next_page_flag: 1,
      };
    }

    if (otherParams.isReflexive) {
      reqParams = omit(reqParams, 'index_modify_flag');
      reqParams = omit(reqParams, 'next_page_flag');
      reqParams = {
        ...reqParams,
        reflexive_question_flag: 1,
        ...(otherParams.calculateClick && {
          calculateClick: otherParams.calculateClick,
        }),
        ...(otherParams.csrConfig && { csrConfig: otherParams.csrConfig }),
      };
      if (listQId) {
        reqParams = {
          ...reqParams,
          list_question_id: listQId,
        };
      }
    }

    if (otherParams.validate) {
      reqParams = omit(reqParams, 'index_modify_flag');
      reqParams = omit(reqParams, 'next_page_flag');
      reqParams = {
        ...reqParams,
        dob_validation_flag: 1,
      };
    }

    if (action) {
      reqParams = omit(reqParams, 'index_modify_flag');
      reqParams = omit(reqParams, 'next_page_flag');
      if (action === 'CANCEL_UPDATE') reqParams = omit(reqParams, 'questions');
      reqParams = {
        ...reqParams,
        action,
      };
    }

    if (activeSubsectionId === 'quotereview') {
      reqParams = omit(reqParams, 'index_modify_flag');
      reqParams = {
        ...reqParams,
        next_page_flag: 1,
      };
    }

    if (isListEditAction) {
      reqParams = {
        ...reqParams,
        index_modify_flag: 1,
        list_question_id: listQId,
      };
    }

    if (!isEmpty(otherParams.actionProps)) {
      const { actionProps } = otherParams;
      reqParams = omit(reqParams, 'index_modify_flag');
      reqParams = omit(reqParams, 'next_page_flag');
      reqParams = {
        ...reqParams,
        ...actionProps.body,
      };
    }
    if (activeSubsectionId === 'signature' && reqParams.next_page_flag) {
      reqParams = {
        ...reqParams,
        signature_completed: true,
      };
    }
    dispatch(
      getNextQuestionSet(url, reqParams, {
        ...otherParams,
        maskedQArray,
        sectionIndex,
        sectionId: get(currentQuestionList[0], 'question_id', ''),
        isListAddAction,
        isListEditAction,
        listIdx,
        ...reqParams,
      }),
    );
  };
};

export const setCurrentBreadcrumb = (id) => ({
  type: SET_CURRENT_BREADCRUMB,
  id,
});

export const toggleESignButton = (enable) => ({
  type: TOGGLE_ESIGN_BUTTON,
  enable,
});

export const setPDFConfigs = (pdfBlobURL, numPages) => ({
  type: SET_PDF_CONFIGS,
  url: pdfBlobURL,
  pages: numPages,
});

const answerIsAMatch = (allAnswers, currentAnswer) => {
  if (isArray(currentAnswer)) {
    return intersection(allAnswers, currentAnswer).length !== 0;
  }
  let answer = currentAnswer;

  if (isObject(currentAnswer)) {
    answer = currentAnswer.id;
  } else if (!Number.isNaN(Number.parseFloat(answer))) {
    answer = Number.parseFloat(currentAnswer);
  }

  answer = allAnswers.filter((a) => {
    try {
      if (isObject(currentAnswer)) {
        return a.toString() === currentAnswer.id.toString();
      }
      return a.toString() === currentAnswer.toString();
    } catch (e) {
      console.error('Error in filter', e);
      return false;
    }
  }).length;
  return answer;
};

/**
 * @description function determines if reflexive question API call
 * should be done.
 * @param {Array} questionsArray existing questions and answers
 * @param {Array} childQuestionsOn options for which child questions
 * will be sent
 * @param {String} currentQuestionId
 * @param {String} currentAnswer
 * @return {Boolean}
 */
export const shouldMakeReflexiveCall = (
  questionsArray,
  childQuestionsOn,
  currentQuestionId,
  currentAnswer,
  ignoreChildQuestionsOn,
  isReflexiveQuestion,
) => {
  const hasAnswer = questionsArray.filter(
    (question) => question.question_id === currentQuestionId,
  )[0];

  const response = hasAnswer ? hasAnswer.response : undefined;

  if ((response || currentAnswer) && ignoreChildQuestionsOn) {
    return true;
  }

  if (childQuestionsOn && answerIsAMatch(childQuestionsOn, currentAnswer)) {
    return true;
  }
  // if there are no answer eligible for reflexive calls, return false.
  if (!childQuestionsOn || (isArray(childQuestionsOn) && childQuestionsOn.length === 0)) {
    return isReflexiveQuestion;
  }

  /**
   * if there is no answer and the current answer is not part
   * of child_questions_on array, do not make the api call.
   * For every other condition, make the api call.
   */
  if (!(response || response === 0) && !answerIsAMatch(childQuestionsOn, currentAnswer)) {
    return false;
  }

  /**
   * if we have an answer and this answer is not in the
   * child_question_on array, AND
   * if the new answer is also not in the child_questions_on
   * array then do not make the api call.
   */
  return (
    response &&
    (answerIsAMatch(childQuestionsOn, hasAnswer.response) ||
      answerIsAMatch(childQuestionsOn, currentAnswer))
  );
};

// returns compiled questionns, answers & errors arrays from all the sections
export const getCompiledQuestionsAnswersErrors = (state) => {
  const sectionsKeys = Object.keys(state.questions.sections).filter(
    (section) => section !== 'testSections',
  );
  const sectionsArr = sectionsKeys.reduce(
    (acc, section) => acc.concat(state.questions.sections[section]),
    [],
  );

  const leftQuestionList = sectionsArr.reduce(
    (acc, group) => acc.concat(group.leftQuestionList || []),
    [],
  );
  const totalQuestionList = sectionsArr.reduce((acc, group) => acc.concat(group.questionList), []);
  const totalErrorList = sectionsArr.reduce((acc, group) => acc.concat(group.errorsArray), []);

  const totalAnswerList = sectionsArr.reduce((acc, group) => acc.concat(group.answersArray), []);

  leftQuestionList.forEach((lQ) => {
    if (!totalAnswerList.find((q) => q.question_id === lQ.question_id)) {
      totalAnswerList.push({
        question_id: lQ.question_id,
        answer: lQ.response,
      });
    }
  });

  return {
    questionList: [...totalQuestionList, ...leftQuestionList],
    answerList: totalAnswerList,
    errorList: totalErrorList,
  };
};

// takes redux state, outputs last answered question
export const getLastAnsweredQuestion = (state, pageId, isAccordionExpanded) => {
  // eslint-disable-next-line prefer-const
  let { answerList = [], questionList = [] } = getCompiledQuestionsAnswersErrors(state);
  answerList = answerList.filter((question) => !!question.answer);
  if (pageId && pageId.match(/quote/i) && !isAccordionExpanded) {
    answerList = answerList.filter((q) => !q?.question_id?.match(/NeedsCalc/i));
  }

  const answerAnsweredQuestion = answerList.pop();
  const answeredQObj = questionList.filter(
    (q) => q?.question_id === answerAnsweredQuestion?.question_id,
  )[0];

  const listQs = questionList.filter((q) => q.display_type === 'list');
  const editableListQ = listQs.filter((q) => q.question_id === state.questions.listEditQId)[0];
  let listQ;
  if (!editableListQ) {
    if (listQs.length === 1) {
      listQ = {
        ...listQs[0],
        listEditMode: false,
      };
    }
  }

  return {
    answerAnsweredQuestion,
    displayType: answeredQObj?.display_type,
    listQ,
  };
};

// takes redux state, outputs first unanswered question
export const getFirstUnansweredQuestion = (state, isSkipNavigation = false) => {
  const { questionList = [], answerList = [] } = getCompiledQuestionsAnswersErrors(state);
  const requiredQs = questionList.filter(
    (q) =>
      !q.is_hidden &&
      (get(q, 'validations.required.value', false) ||
        (q.display_type === 'list' && !q.child_questions_completed_flag)),
  );
  const unansweredQs = requiredQs.filter((q) => {
    const answer = get(
      answerList.find((ans) => q.question_id === ans.question_id),
      'answer',
    );
    return !(answer || answer === 0);
  });
  const listQs = questionList.filter((q) => q.display_type === 'list');

  // 1. check if any listQ is in edit mode & scroll to that
  const editableListQ = listQs.filter((q) => q.question_id === state.questions.listEditQId)[0];
  if (editableListQ && !isSkipNavigation) return editableListQ;

  // 2. Check if the listQs have unanswered original_questions || question & scroll to that
  // Scenario: When editing: Policy Information > Primary Beneficiary > Type of Entity
  if (listQs.length) {
    const listQ = listQs[0];
    const getUnansweredQListResponse = (listQuestionList, isChildQsCompleted) => {
      const unansweredQList = listQuestionList.filter(
        (q) => !q.response && q.display_type !== 'label',
      );
      const nestedUnansweredQList = listQuestionList.filter(
        (q) => !!q.response && q.questions.length,
      );
      // Any nested question present inside deeper level, send this to check deeper level
      if (nestedUnansweredQList.length) {
        const nestedUnansweredQListRecursiveRes = getUnansweredQListResponse(
          nestedUnansweredQList[0].questions,
        );
        if (nestedUnansweredQListRecursiveRes) return nestedUnansweredQListRecursiveRes;
      }
      // If above condition not met and there is unanswered question in current level, return
      const skipNavListConditions =
        !isSkipNavigation ||
        (isSkipNavigation && !isChildQsCompleted) ||
        (isSkipNavigation && editableListQ);
      if (unansweredQList.length && skipNavListConditions) return unansweredQList[0];
      // If both above condition not met and nesting of listQs is present,
      if (listQuestionList[0].questions.length)
        return getUnansweredQListResponse(listQuestionList[0].questions);
      // If all questions answered, ignore scroll
      return null;
    };
    const unansweredQList = getUnansweredQListResponse(
      listQ.original_questions || listQ.questions,
      listQ.child_questions_completed_flag,
    );
    if (unansweredQList) return unansweredQList;
  }

  // 3. check for requiredQs with required validation or if listQs are displayed
  if (requiredQs.length) {
    if (unansweredQs.length && unansweredQs[0].display_type === 'list') {
      if (unansweredQs[0].child_questions_completed_flag) return unansweredQs[1];
      return unansweredQs[0];
    }
    return unansweredQs[0];
  }
  return unansweredQs[0];
};

const getParentOfReflexiveQs = (questions) => {
  const parentQuestions = questions.filter((q) => q.child_questions && q.child_questions_on.length);
  return parentQuestions;
};

// Get q.questions for which q.child_questions = true
// AND q.child_questions_on=[q1, q2, q3, ...]
const getAllChildQuestions = (questions) => {
  const parentQuestions = getParentOfReflexiveQs(questions);
  let childQuestions = [];
  if (parentQuestions.length) {
    parentQuestions.forEach((q) => {
      childQuestions = [...childQuestions, ...q.questions];
    });
  }
  return childQuestions;
};

const searchForListParentQ = (questionList, listParentId) => {
  let listParentQ = questionList.filter((q) => q.question_id === listParentId);
  let childQuestions = [];
  if (!listParentQ.length) {
    let listQs = questionList.filter((q) => q.display_type === 'list');
    if (listQs.length) {
      let questions = [];
      listQs.forEach((q) => {
        if (q.child_questions_completed_flag) {
          questions = flattenDepth(q.questions);
        } else {
          questions = flattenDeep(q.original_questions);
        }
      });
      if (questions.length) {
        listParentQ = questions.filter((q) => q.question_id === listParentId);
      }
      if (!listParentQ.length) {
        childQuestions = getAllChildQuestions(questions);
        if (childQuestions.length) {
          listParentQ = childQuestions.filter((q) => q.question_id === listParentId);
        }
      }
    }
    if (!listParentQ.length) {
      childQuestions = getAllChildQuestions(questionList);
      if (childQuestions.length) {
        listParentQ = childQuestions.filter((q) => q.question_id === listParentId);
        if (!listParentQ.length) {
          listQs = childQuestions.filter((q) => q.display_type === 'list');
          listParentQ = listQs.filter((q) => q.question_id === listParentId);
        }
      }
    }
  }
  return listParentQ[0];
};

export const updateAnswersArray = (
  payload = {
    sectionIndex: 0,
    response: '',
    questionId: '',
    triggerValidation: null,
    hasEdit: false,
    hasMask: false,
    actualResponse: '',
    questionResponse: {},
    preventReflexiveCall: false,
    isError: false,
    validate: false,
    ignoreChildQuestionsOn: false,
    propQuestion: null,
    listParentId: '',
    listIndex: null,
    currentStatus: null,
    displayType: null,
    auditRequired: false,
    makeApiCall: false,
    defaultCsrConfig: null,
    parentListIndex: null,
    gridCellId: null,
  },
) => {
  return (dispatch, getState) => {
    if (payload.response || payload.isError || payload.response === '' || payload.response === 0) {
      const currentState = getState();
      const { breadcrumbs, activeSubsectionId, currentPageSeqNumber, eSign, role } =
        currentState.questions;
      const currentBreadcrumbId = get(breadcrumbs, 'currentBreadcrumb.id', '');
      const {
        sectionIndex,
        response,
        questionId,
        preventReflexiveCall,
        validate,
        ignoreChildQuestionsOn,
        propQuestion,
        listParentId,
        listIndex,
        auditRequired,
        hasEdit,
        makeApiCall,
        defaultCsrConfig,
        triggerValidation,
        isNestedQ,
        parentListIndex,
        gridCellId,
      } = payload;

      const currentSubSection = currentState.questions.sections[activeSubsectionId][sectionIndex];
      const { questionList } = currentSubSection;

      if (hasEdit || gridCellId) {
        const responseId = get(response, 'id', '');
        dispatch({
          type: UPDATE_LAST_FOCUSED_ELEMENT_ID,
          payload: {
            id: gridCellId || responseId || questionId,
          },
        });
      }

      if (questionId.indexOf('_$ureify_') > -1) {
        dispatch({
          type: UPDATE_QUESTION_ANSWER,
          currentBreadcrumbId,
          currentPageSeqNumber,
          payload: {
            ...payload,
            questionId,
            propQuestion,
            auditRequired,
            eSign,
          },
          activeSubsectionId,
        });
        if (!preventReflexiveCall && propQuestion.child_questions) {
          dispatch(
            submitSectionQuestion(sectionIndex, {
              isReflexive: true,
              sectionIndex,
              preventReflexiveCall: true,
            }),
          );
        }
        return;
      }

      let question;
      let listQ = null;
      let questionsArray = questionList;

      // if (listParentId && Number.isInteger(listIndex) && listIndex >= 0) {
      //   const listParentQ = questionList.filter((q) => q.question_id === listParentId)[0];
      //   let listQ = listParentQ.questions[listIndex];
      //   // Fix- list questions coming in object, rather than in array
      //   if (!Array.isArray(listQ)) {
      //     listQ = Object.values(listQ).filter((q) => q);
      //   }
      //   // flatten questions in case any question in the list might have nested child questions
      //   const flattenedListQ = [];
      //   HELPER.flattenQuestions(listQ, flattenedListQ);
      //   questionsArray = flattenedListQ;
      //   [question] = flattenedListQ.filter((q) => q.question_id === questionId);
      // } else if (listParentId) {
      //   const listParentQ = questionList.filter((q) => q.question_id === listParentId)[0];
      //   const listQ = listParentQ.original_questions
      //     ? listParentQ.original_questions
      //     : listParentQ.questions;
      //   // flatten questions in case any question in the list might have nested child questions
      //   const flattenedListQ = [];
      //   HELPER.flattenQuestions(listQ, flattenedListQ);
      //   [question] = flattenedListQ.filter((q) => q.question_id === questionId);
      //   questionsArray = flattenedListQ;
      // } else {
      //   [question] = questionList.filter((q) => q.question_id === questionId);
      // }

      if (listParentId) {
        const listParentQ = searchForListParentQ(questionList, listParentId);
        const originalQuestions = listParentQ?.original_questions?.length
          ? listParentQ.original_questions
          : null;
        if (Number.isInteger(listIndex) && listIndex >= 0) {
          listQ = listParentQ?.questions[listIndex];
        } else {
          listQ = originalQuestions || listParentQ.questions;
        }
        const flattenedListQ = [];
        flattenQuestions(listQ, flattenedListQ);
        questionsArray = flattenedListQ;
        [question] = flattenedListQ.filter((q) => q.question_id === questionId);
      } else {
        [question] = questionList.filter((q) => q.question_id === questionId);
      }

      if (!question && Array.isArray(listQ)) {
        let nestedReflexQs = [];
        listQ.forEach((q) => {
          if (q.questions && q.questions.length) {
            nestedReflexQs = [...nestedReflexQs, ...q.questions];
          }
        });
        [question] = nestedReflexQs.filter((que) => que.question_id === questionId);
        questionsArray = nestedReflexQs;
      }

      const parentId = get(question, 'properties.parent_group_id', '');
      const parentQuestion = questionList.filter((q) => q.question_id === parentId)[0];

      let isDataSelector = false;
      const currentQuestionSet = get(parentQuestion, 'questions', []);
      if (currentQuestionSet.length > 0) {
        isDataSelector = currentQuestionSet[0].display_type === 'data_selector';
      }

      let reflexCall = false;
      if (
        !preventReflexiveCall &&
        question &&
        (question.child_questions || question.reflexive_question_flag)
      ) {
        reflexCall = shouldMakeReflexiveCall(
          questionsArray,
          question.child_questions_on,
          questionId,
          response,
          ignoreChildQuestionsOn,
          question.reflexive_question_flag,
        );
      }

      // csr quote screen
      const ctaBtnQuestionId = get(question, 'properties.cta_button.value');
      const csrGroup = get(question, 'properties.group.value');
      const csrConfigKey = csrGroup === 'requote' ? 'requoteCsrConfig' : 'csrConfig';

      let revisedCsrConfig = {};
      let areCSRAnswersValid = false;
      const {
        questionList: allQuestions,
        answerList,
        errorList: errorsArray,
      } = getCompiledQuestionsAnswersErrors(currentState);

      if (defaultCsrConfig) {
        revisedCsrConfig = defaultCsrConfig;
      } else if (ctaBtnQuestionId) {
        // const { answersArray, errorsArray } = currentSubSection;
        const allAnswers = HELPER.addOrReplace(
          answerList,
          {
            question_id: questionId,
            answer: response,
            immediateParent: propQuestion ? propQuestion.immediateParent : '',
            parentId: propQuestion ? propQuestion.parentId : '',
            parentListQuestionId: get(propQuestion, 'properties.parent_list_id', ''),
            listParentId,
            listIndex,
          },
          'answer',
        );

        const ctabBtnQuestion = allQuestions.filter((q) => q.question_id === ctaBtnQuestionId)[0];
        const requiredCsrSchema = get(ctabBtnQuestion, 'properties.required_questions_list', []);
        const csrKey = get(ctabBtnQuestion, 'properties.csr.value');

        if (ctabBtnQuestion && csrKey && requiredCsrSchema && requiredCsrSchema.length) {
          const requiredQuestionObjs = allQuestions.filter((q) =>
            requiredCsrSchema.includes(q.question_id),
          );
          const requiredQuestionList = requiredQuestionObjs.filter(
            (q) => get(q, 'validations.required.value', false) && !q.is_hidden,
          );
          // const actualRequiredAnswers = allAnswers.filter((ans) =>
          //   requiredQuestionList.find((q) => q.question_id === ans.question_id),
          // );

          const isRequiredQsAnswered = requiredQuestionList.every((reqQ) => {
            const ans = allAnswers.find((q) => q.question_id === reqQ.question_id)?.answer;
            return ans || ans === 0;
          });

          const errorList = HELPER.addOrReplace(
            errorsArray,
            {
              question_id: questionId,
              isError: payload.isError,
            },
            'isError',
          ).filter((errorElement) => errorElement && errorElement.isError);

          // check if all required answers are valid with no errors
          const isValid =
            !errorList.some((err) => requiredCsrSchema.includes(err.question_id)) &&
            isRequiredQsAnswered;
          areCSRAnswersValid = isValid;

          if (requiredCsrSchema.includes(questionId) && csrKey) {
            const csrSection = get(question, 'properties.section.value');
            if (csrKey === 'rateClass') {
              revisedCsrConfig = {
                rateClass: {
                  enable: isValid,
                  show: false,
                },
                quoteResults: {
                  enable: false,
                  show: false,
                },
                quoteSummary: {
                  enable: false,
                  show: false,
                },
              };
            } else if (csrKey === 'quoteResults' && (!csrSection || csrSection === 'rateClass')) {
              let quoteResultsQs = allQuestions.filter(
                (q) =>
                  get(q, 'properties.section.value', '') === 'quoteResults' &&
                  get(q, 'properties.group.value') !== 'requote',
              );
              if (csrGroup === 'requote') {
                quoteResultsQs = allQuestions.filter(
                  (q) =>
                    get(q, 'properties.section.value', '') === 'quoteResults' &&
                    get(q, 'properties.group.value') === 'requote',
                );
              }
              // eslint-disable-next-line no-empty
              if (quoteResultsQs.every((q) => q.is_hidden)) {
              } else {
                revisedCsrConfig = {
                  quoteResults: {
                    enable: isValid,
                    show: false,
                  },
                };
                if (hasEdit) {
                  revisedCsrConfig.quoteSummary = {
                    enable: false,
                    show: false,
                  };
                }
              }
            } else if (csrKey === 'quoteSummary' && csrSection === 'quoteResults') {
              revisedCsrConfig = {
                quoteSummary: {
                  enable: isValid,
                  show: false,
                },
              };
            }
          }
        }
      }
      // preseve csrConfig for reflexive call
      let reflexiveCsrConfig = null;
      const prevResp = get(
        answerList.find((ans) => ans.questionId === questionId),
        'answer',
      );
      const currentResp = payload.response;
      // check if question response is changed
      if (prevResp !== currentResp && isCustomRole(role) && ctaBtnQuestionId) {
        reflexiveCsrConfig = {
          ctaBtnId: ctaBtnQuestionId,
          enabled: areCSRAnswersValid,
        };
      }

      dispatch({
        type: UPDATE_QUESTION_ANSWER,
        currentBreadcrumbId,
        currentPageSeqNumber,
        payload: {
          ...payload,
          auditRequired,
          eSign,
          [csrConfigKey]: revisedCsrConfig,
          triggerValidation,
          isNestedQ,
          parentListIndex,
        },
        activeSubsectionId,
      });

      if (isDataSelector) {
        dispatch({
          type: UPDATE_DATA_SELECTOR_OPTIONS,
          currentBreadcrumbId,
          currentPageSeqNumber,
          payload: {
            ...payload,
            questionId,
            parentQuestion,
          },
          activeSubsectionId,
        });
      }

      if (payload.currentStatus === 'products' && payload.displayType === 'product_card') {
        dispatch(submitSectionQuestion(sectionIndex));
        return;
      }
      if (
        process.env.REACT_APP_ENABLE_ETL === 'true' &&
        (reflexCall || (question?.display_type === 'date' && question?.reflexive_question_flag))
      ) {
        const transactionLog = propQuestion?.transaction_log;
        const { meta } = currentState;
        const { os, userAgent, platform } = meta;
        const previousTransactionLog =
          transactionLog && transactionLog.length ? transactionLog : [];
        const timeStampData = {
          transactionLog: [
            ...previousTransactionLog,
            {
              time_start: getUTCTimestamp(),
              time_end: getUTCTimestamp(),
              os,
              browser: userAgent,
              platform,
            },
          ],
          lastSubmittedAt: getUTCTimestamp(),
        };
        dispatch({
          type: UPDATE_QUESTION_ANSWER_TIME_STAMP,
          payload: {
            questionId,
            timeStampData,
            activeSubsectionId,
            sectionIndex,
            meta,
          },
        });
      }

      // check if reflexive answer
      if (!preventReflexiveCall || makeApiCall) {
        if (reflexCall || makeApiCall) {
          dispatch(
            submitSectionQuestion(sectionIndex, {
              isReflexive: true,
              sectionIndex,
              preventReflexiveCall: true,
              listIndex,
              listParentId,
              isList: !!listParentId,
              isContingentBenf: questionId.includes('ContingentBeneficiary'),
              questionId,
              ...(reflexiveCsrConfig && { csrConfig: reflexiveCsrConfig }),
            }),
          );
        }
      }
      if (validate && response) {
        dispatch(
          submitSectionQuestion(sectionIndex, {
            validate: true,
            sectionIndex,
            preventReflexiveCall: true,
            listIndex,
            listParentId,
            isList: !!listParentId,
            isContingentBenf: questionId.includes('ContingentBeneficiary'),
            questionId,
          }),
        );
      }
    }
  };
};

export const makeQuestionsCall = () => {
  return (dispatch, getState) => {
    const currentState = getState();
    const { uid } = currentState;
    const { breadcrumbs, currentPageSeqNumber, breadcrumbIndex } = currentState.questions;
    const { breadcrumbList } = breadcrumbs;
    const currentBreadcrumbIndex = breadcrumbIndex - 1;
    if (currentBreadcrumbIndex > -1 && breadcrumbList[currentBreadcrumbIndex].clickable) {
      const currentBreadcrumb = breadcrumbList[currentBreadcrumbIndex];
      const params = {
        uid,
        breadcrumb_id: currentBreadcrumb.breadcrumb_id,
        breadcrumb_nav_flag: true,
        page_sequence_number: currentPageSeqNumber,
      };
      dispatch(setCurrentBreadcrumb(currentBreadcrumb.breadcrumb_id));
      dispatch(getNextQuestionSet('/questions', params));
    }
  };
};

export const getNextPage = (params) => {
  return (dispatch, getState) => {
    const { uid, questions } = getState();
    const { currentPageSeqNumber } = questions;

    const reqParams = {
      uid,
      page_sequence_number: currentPageSeqNumber,
      ...params,
    };

    dispatch(
      getNextQuestionSet('/questions', reqParams, {
        ...reqParams,
      }),
    );
  };
};

export const goToPreviousBreadCrumb = (sectionIndex, otherProps) => {
  return (dispatch, getState) => {
    const currentState = getState();
    const { currentPageSeqNumber } = currentState.questions;
    const { uid } = currentState;
    const reqParams = {
      uid,
      page_sequence_number: currentPageSeqNumber,
      prev_page_flag: true,
    };
    dispatch(
      getNextQuestionSet(
        '/questions',
        {
          ...reqParams,
          ...otherProps,
        },
        {
          ...reqParams,
          sectionIndex,
        },
        reqParams,
      ),
    );
  };
};

export const setOuterListActionButton = (shouldEnable, sectionIndex = null) => ({
  type: SET_OUTER_LIST_ACTION_BUTTON,
  payload: {
    shouldEnable,
    sectionIndex,
  },
});

export const setCurrentEditListIndices = (
  parentListIndex,
  childListIndex,
  sectionIndex,
  isAddAction,
  listParentId,
) => ({
  type: SET_CURRENT_EDITING_LIST_INDICES,
  payload: {
    editingParentListIndex: parentListIndex,
    editingChildListIndex: childListIndex,
    editingSectionListIndex: sectionIndex,
    isAddAction,
    listParentId,
  },
});

export const resetCurrentEditListIndices = () => ({
  type: RESET_CURRENT_EDITING_LIST_INDICES,
});
